import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import AudioSourcePicker from "@/components/sunovoice/AudioSourcePicker";
import SegmentPicker, { segmentProblem } from "@/components/sunovoice/SegmentPicker";
import VoiceDetailsFields, { lbl } from "@/components/sunovoice/VoiceDetailsFields";
import { createSunoVoice, uploadVoiceAudio } from "@/lib/sunoVoicesApi";
import { safeApiError } from "@/lib/safeErrorText";

const START = { name: "Apostle Moore", skill: "advanced", language: "en", style: "" };

/** Step 1 — your singing (recorded or uploaded) + the stretch ELPM learns from → ELPM writes your check phrase. */
export default function NewSunoVoiceDialog({ open, onClose }) {
  const navigate = useNavigate();
  const [details, setDetails] = useState(START);
  const [file, setFile] = useState(null);
  const [duration, setDuration] = useState(null);
  const [range, setRange] = useState([0, 60]);
  const [busy, setBusy] = useState(null);
  const problem = file ? segmentProblem(range, duration) : null;
  const clear = () => { setFile(null); setDuration(null); setRange([0, 60]); };
  const go = async () => {
    try {
      setBusy("Uploading your singing…");
      const url = await uploadVoiceAudio(file);
      setBusy("Sending it to ELPM…");
      const v = await createSunoVoice({ ...details, name: details.name.trim(), style: details.style.trim() || null,
        source_url: url, source_label: file.name.slice(0, 120), start: range[0], end: range[1] });
      toast.success("ELPM is listening", { description: "Your check phrase arrives in about a minute." });
      navigate(`/create/my-voices/elpm/${v.id}`);
    } catch (e) { toast.error("Couldn't start your ELPM voice", { description: safeApiError(e) }); setBusy(null); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent data-testid="new-suno-voice-dialog" className="max-h-[92vh] max-w-lg overflow-y-auto border-white/10 bg-[#0b0e1c] text-white">
        <DialogHeader>
          <DialogTitle className="font-display">Teach ELPM your singing voice</DialogTitle>
          <DialogDescription className="text-white/60">Give ELPM one stretch of you singing clearly — ideally a cappella or with soft music. Next, you'll sing back a short phrase to prove it's you.</DialogDescription>
        </DialogHeader>
        <span className={lbl}>Your singing</span>
        {file ? <SegmentPicker file={file} range={range} onRange={setRange} duration={duration} onDuration={setDuration} onClear={clear} />
          : <AudioSourcePicker testId="suno-voice-source" maxSec={180} onFile={setFile} hint="Sing a verse or chorus you know well, close to the mic, with no music playing." />}
        {problem ? <p data-testid="segment-problem" className="mt-2 flex items-start gap-1.5 text-[12px] text-amber-200/85"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{problem}</p>
          : file ? <p className="mt-2 text-[11px] text-white/45">Best: 20–60 seconds where only you are heard, clearly.</p> : null}
        <VoiceDetailsFields value={details} onChange={setDetails} />
        <button type="button" data-testid="suno-voice-create" onClick={go} disabled={Boolean(busy) || !file || !duration || Boolean(problem) || !details.name.trim()}
          className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-cyan-300 py-3 text-[13px] font-semibold text-black transition-transform duration-200 hover:scale-[1.01] disabled:opacity-40 disabled:hover:scale-100">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {busy || "Get my check phrase"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
